import React, { useEffect } from 'react';
import { Container } from 'react-bootstrap';
import './LegalPages.css';

function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <Container className="legal-page">
      <div className="legal-content">
        <div className="legal-header">
          <h1>Privacy Policy for ZDIVE</h1>
          <p className="legal-date">Last Updated: August, 2026</p>
        </div>

        <section className="legal-section">
          <h2>Introduction</h2>
          <p>
            ZDIVE ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our diving application and related services (collectively, the "Service").
          </p>
        </section>

        <section className="legal-section">
          <h2>Information We Collect</h2>

          <div className="legal-subsection">
            <h3>Health and Fitness Data</h3>
            <ul>
              <li><strong>Heart Rate Data:</strong> We collect your heart rate information during dives to track dive fitness metrics and provide safety insights</li>
              <li><strong>Blood Oxygen Levels:</strong> We monitor blood oxygen saturation to assess your physiological condition during dives</li>
              <li><strong>Workout Data:</strong> We save your dive sessions as workout data in Apple Health to track your diving activity</li>
              <li><strong>Health Metrics:</strong> We may collect other health-related metrics relevant to diving safety</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Location Data</h3>
            <ul>
              <li><strong>GPS Coordinates:</strong> We collect your precise location to record dive sites and create accurate dive logs</li>
              <li><strong>Dive Site Information:</strong> We store location data to help you identify and return to dive sites</li>
              <li><strong>Route Tracking:</strong> We track your movement during dives to create detailed dive routes and breadcrumb trails</li>
              <li><strong>Compass Data:</strong> We use location services to provide compass heading functionality</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Motion and Sensor Data</h3>
            <ul>
              <li><strong>Depth Measurements:</strong> We collect depth data from motion sensors to track your dive profile</li>
              <li><strong>Water Temperature:</strong> We monitor water temperature during your dives</li>
              <li><strong>Motion Patterns:</strong> We use motion sensors to detect water submersion and track dive conditions</li>
              <li><strong>Device Orientation:</strong> We collect orientation data to provide accurate depth readings</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Dive-Specific Data</h3>
            <ul>
              <li><strong>Dive Sessions:</strong> Complete records of your dives including timestamps, duration, and conditions</li>
              <li><strong>Decompression Data:</strong> Calculations and recommendations for decompression stops</li>
              <li><strong>Equipment Information:</strong> Details about your diving equipment and configurations</li>
              <li><strong>Dive Plans:</strong> Your planned dives and trip information</li>
              <li><strong>Packing Lists:</strong> Equipment checklists and trip preparation data</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>How We Use Your Information</h2>

          <div className="legal-subsection">
            <h3>Core Functionality</h3>
            <ul>
              <li><strong>Dive Tracking:</strong> Recording and analyzing your dive sessions</li>
              <li><strong>Safety Monitoring:</strong> Providing real-time safety alerts and decompression calculations</li>
              <li><strong>Dive Logging:</strong> Creating comprehensive dive logs and history</li>
              <li><strong>Equipment Management:</strong> Helping you track and manage your diving equipment</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Data Synchronization</h3>
            <ul>
              <li><strong>Cross-Device Sync:</strong> Synchronizing your dive data between iPhone and Apple Watch</li>
              <li><strong>Cloud Backup:</strong> Storing your data securely in iCloud for backup and recovery</li>
              <li><strong>Data Recovery:</strong> Ensuring your dive data is not lost if you change devices</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Safety and Health</h3>
            <ul>
              <li><strong>Decompression Calculations:</strong> Using Bühlmann ZH-L16C and other algorithms to calculate safe ascent profiles</li>
              <li><strong>Health Integration:</strong> Integrating with Apple Health to provide comprehensive health insights</li>
              <li><strong>Safety Alerts:</strong> Providing warnings for potentially dangerous dive conditions</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>Data Storage and Security</h2>

          <div className="legal-subsection">
            <h3>Local Storage</h3>
            <ul>
              <li>Your dive data is stored locally on your device using Core Data</li>
              <li>Local storage ensures your data is available even without internet connectivity</li>
              <li>Data is encrypted using iOS's built-in encryption mechanisms</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Cloud Storage</h3>
            <ul>
              <li>We use Apple's iCloud service (CloudKit) to synchronize your data across devices</li>
              <li>Your data is stored in your personal iCloud account</li>
              <li>CloudKit provides enterprise-grade security and encryption</li>
              <li>Data is only accessible to you and devices signed into your Apple ID</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>Third-Party Services</h2>

          <div className="legal-subsection">
            <h3>Apple Services</h3>
            <ul>
              <li><strong>HealthKit:</strong> We integrate with Apple Health to read and write health data</li>
              <li><strong>CloudKit:</strong> We use Apple's CloudKit for data synchronization</li>
              <li><strong>Core Location:</strong> We use Apple's location services for GPS functionality</li>
              <li><strong>Core Motion:</strong> We use Apple's motion framework for sensor data</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Safety Configuration Checks</h3>
            <ul>
              <li>The app periodically fetches a safety configuration record (minimum safe app versions and safety advisories) from Apple's CloudKit public database, and checks the current App Store version via Apple's iTunes lookup service</li>
              <li>These requests exist so we can warn you about safety-critical updates; they contain no personal data and no dive data</li>
              <li>Crash and performance diagnostics (Apple MetricKit) are stored only on your device and never leave it unless you explicitly export them</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Google Mobile Ads</h3>
            <ul>
              <li>We use Google Mobile Ads SDK for displaying advertisements</li>
              <li>Ad personalization is based on your device's advertising identifier</li>
              <li>You can opt out of personalized ads in your device settings</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Website Analytics</h3>
            <ul>
              <li>On our website (zdive.app), we use Google Analytics to collect aggregated, anonymized usage statistics to improve website performance and user experience.</li>
              <li>We do not sell your personal data to advertisers or data brokers.</li>
              <li>Within the mobile application, your dive data, sensor logs, and Apple Health information remain strictly private and are never shared with third-party tracking services.</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>Your Rights and Choices</h2>

          <div className="legal-subsection">
            <h3>Data Access and Control</h3>
            <ul>
              <li>You can view all your dive data within the app</li>
              <li>You can export your dive data through the app's export functionality</li>
              <li>You can delete individual dive sessions or your entire dive log</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Privacy Settings</h3>
            <ul>
              <li>You can control location permissions in your device settings</li>
              <li>You can manage HealthKit permissions in the Health app</li>
              <li>You can disable iCloud sync in the app settings</li>
              <li>You can opt out of personalized ads in your device settings</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>Contact Us</h2>
          <p>If you have any questions about this Privacy Policy or our data practices, please contact us at:</p>
          <div className="contact-info">
            <p><strong>Email:</strong> dive@zyork.dev</p>
            <p><strong>Website:</strong> https://zdive.app/</p>
          </div>
        </section>

        <section className="legal-section">
          <h2>Compliance</h2>
          <p>This Privacy Policy complies with applicable privacy laws and regulations, including:</p>
          <ul>
            <li>General Data Protection Regulation (GDPR)</li>
            <li>California Consumer Privacy Act (CCPA)</li>
            <li>Children's Online Privacy Protection Act (COPPA)</li>
            <li>Apple's App Store Review Guidelines</li>
          </ul>
        </section>

        <div className="legal-footer">
          <p>
            This Privacy Policy is effective as of the date listed above and will remain in effect except with respect to any changes in its provisions in the future, which will be in effect immediately after being posted on this page.
          </p>
        </div>
      </div>
    </Container>
  );
}

export default PrivacyPolicy;